import { configClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  CostApprovalSettings,
  CostApprovalSettingsHistoryPage,
  CostApprovalSettingsHistoryPageWire,
  CostApprovalSettingsValues,
  CostApprovalSettingsWire,
  CostApprovalSettingsWriteBody,
} from '../types/cost-approval-settings.types';

/** Maps the snake_case wire row to the camelCase domain shape. */
function toSettings(w: CostApprovalSettingsWire): CostApprovalSettings {
  return {
    enabled: w.enabled,
    alertThresholdUsd: w.alert_threshold_usd,
    approvalThresholdUsd: w.approval_threshold_usd,
    alertSlackChannelId: w.alert_slack_channel_id,
    approvalSlackChannelId: w.approval_slack_channel_id,
    sampleSize: w.sample_size,
    reminderAfterHours: w.reminder_after_hours,
    expireAfterHours: w.expire_after_hours,
    showCostInUi: w.show_cost_in_ui ?? null,
    updatedAt: w.updated_at,
    updatedBy: w.updated_by,
  };
}

/** Fetches the global cost-approval settings (defaults until first saved). */
export async function fetchCostApprovalSettings(): Promise<CostApprovalSettings> {
  const wire = await configClient
    .get(endpoints.costApprovalSettings.settings())
    .json<CostApprovalSettingsWire>();
  return toSettings(wire);
}

/** Acting user recorded in the settings audit trail (`updated_by`). */
export interface CostApprovalWriteActor {
  userId: string;
  /** The user's email — agentic-config stores it as `updated_by`. */
  userName: string;
  reason?: string;
}

/** Replaces the settings (full PUT); the backend 422s on invalid values. */
export async function updateCostApprovalSettings(
  values: CostApprovalSettingsValues,
  actor: CostApprovalWriteActor,
): Promise<CostApprovalSettings> {
  const body: CostApprovalSettingsWriteBody = {
    enabled: values.enabled,
    alert_threshold_usd: values.alertThresholdUsd,
    approval_threshold_usd: values.approvalThresholdUsd,
    alert_slack_channel_id: values.alertSlackChannelId,
    approval_slack_channel_id: values.approvalSlackChannelId,
    sample_size: values.sampleSize,
    reminder_after_hours: values.reminderAfterHours,
    expire_after_hours: values.expireAfterHours,
    // Only to a server that knows the field — an older one 422s on it (`extra: forbid`).
    ...(values.showCostInUi === null ? {} : { show_cost_in_ui: values.showCostInUi }),
    user_id: actor.userId,
    user_name: actor.userName,
    ...(actor.reason?.trim() ? { reason: actor.reason.trim() } : {}),
  };
  const wire = await configClient
    .put(endpoints.costApprovalSettings.settings(), { json: body })
    .json<CostApprovalSettingsWire>();
  return toSettings(wire);
}

/** Fetches one newest-first page of the settings change history. */
export async function fetchCostApprovalSettingsHistory(params: {
  limit: number;
  offset: number;
}): Promise<CostApprovalSettingsHistoryPage> {
  const searchParams = new URLSearchParams();
  searchParams.set('limit', String(params.limit));
  searchParams.set('offset', String(params.offset));
  const page = await configClient
    .get(endpoints.costApprovalSettings.history(), { searchParams })
    .json<CostApprovalSettingsHistoryPageWire>();
  return {
    total: page.total,
    limit: page.limit,
    offset: page.offset,
    items: page.items.map((w) => ({
      id: w.id,
      createdAt: w.created_at,
      userId: w.user_id,
      userName: w.user_name,
      reason: w.reason,
      changes: w.changes,
    })),
  };
}
